// frontend/src/pages/recursos/RecursosMateriales.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  VideoLibrary as VideoIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  Folder as FolderIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import api from '../../services/api';

const RecursosMateriales = () => {
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [videoSeleccionado, setVideoSeleccionado] = useState(null);
  const [reproduciendo, setReproduciendo] = useState(false);
  const [carpetas, setCarpetas] = useState([]);
  const [rutaActual, setRutaActual] = useState('');
  const [breadcrumb, setBreadcrumb] = useState([{ nombre: 'Inicio', ruta: '' }]);

  // Función para construir URL completa del video
  const construirUrlVideo = (video) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    return `${baseUrl}${video.url}`;
  };

  useEffect(() => {
    cargarContenido();
  }, []);

  const cargarContenido = async (ruta = '') => {
      try {
        setLoading(true);
        const response = await api.get('/recursos-materiales/videos-directorio', {
          params: { ruta }
        });
        setCarpetas(response.data.carpetas || []);
        setVideos(response.data.videos || []);
        setRutaActual(response.data.rutaActual || '');
        setBreadcrumb(response.data.breadcrumb || [{ nombre: 'Inicio', ruta: '' }]);
      } catch (error) {
        console.error('Error al cargar contenido:', error);
        setCarpetas([]);
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    const navegarACarpeta = (ruta) => {
      cargarContenido(ruta);
    };

  const handleReproducirVideo = (video) => {
    
    setVideoSeleccionado(video);
    setReproduciendo(true);
  };

  const handleCerrarReproductor = () => {
    setVideoSeleccionado(null);
    setReproduciendo(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <VideoIcon fontSize="large" />
              Videos Disponibles
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {carpetas.length} carpeta(s) y {videos.length} video(s)
            </Typography>
          </Box>
            <IconButton onClick={() => cargarContenido(rutaActual)} color="primary" size="large">
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Breadcrumb de navegación */}
        {breadcrumb.length > 1 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            {breadcrumb.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Typography color="text.secondary">/</Typography>}
                <Button
                  size="small"
                  onClick={() => navegarACarpeta(item.ruta)}
                  sx={{ 
                    minWidth: 'auto', 
                    textTransform: 'none',
                    fontWeight: index === breadcrumb.length - 1 ? 'bold' : 'normal'
                  }}
                  disabled={index === breadcrumb.length - 1}
                >
                  {item.nombre}
                </Button>
              </React.Fragment>
            ))}
          </Box>
        )}

        {/* Grid de videos */}
        {/* Grid de contenido */}
        {carpetas.length === 0 && videos.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <VideoIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Carpeta vacía
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No hay carpetas ni videos en esta ubicación
            </Typography>
            {rutaActual && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navegarACarpeta(breadcrumb[breadcrumb.length - 2]?.ruta || '')}
                sx={{ mt: 2 }}
              >
                Volver
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {/* Carpetas primero */}
            {carpetas.map((carpeta) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={carpeta.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => navegarACarpeta(carpeta.ruta)}
                >
                  <CardMedia
                    sx={{
                      height: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'primary.light'
                    }}
                  >
                    <FolderIcon sx={{ fontSize: 80, color: 'primary.main' }} />
                  </CardMedia>
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" noWrap title={carpeta.nombre}>
                      {carpeta.nombre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Carpeta
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            
            {/* Videos después */}
            {videos.map((video) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={video.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    sx={{
                      height: 180,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.900',
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover .play-overlay': {
                        opacity: 1
                      }
                    }}
                    onClick={() => handleReproducirVideo(video)}
                  >
                    <VideoIcon sx={{ fontSize: 60, color: 'grey.600' }} />
                    <Box
                      className="play-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        opacity: 0,
                        transition: 'opacity 0.3s'
                      }}
                    >
                      <PlayIcon sx={{ fontSize: 60, color: 'white' }} />
                    </Box>
                  </CardMedia>
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" noWrap gutterBottom title={video.nombreSinExtension}>
                      {video.nombreSinExtension}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {video.tamano_formateado}
                    </Typography>
                  </CardContent>
                  
                  <CardActions>
                    <Button
                      size="small"
                      startIcon={<PlayIcon />}
                      onClick={() => handleReproducirVideo(video)}
                      fullWidth
                      variant="contained"
                    >
                      Reproducir
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Diálogo de reproducción de video */}
      <Dialog
        open={reproduciendo}
        onClose={handleCerrarReproductor}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { 
            bgcolor: 'black',
            minHeight: '80vh'
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {videoSeleccionado && (
            <>
              <Box sx={{ 
                position: 'absolute', 
                top: 10, 
                right: 10, 
                zIndex: 1,
                display: 'flex',
                gap: 1
              }}>
                <IconButton 
                  onClick={() => {
                    const videoElement = document.querySelector('video');
                    if (videoElement) {
                      if (videoElement.requestFullscreen) {
                        videoElement.requestFullscreen();
                      }
                    }
                  }}
                  sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}
                >
                  <FullscreenIcon />
                </IconButton>
                <IconButton 
                  onClick={handleCerrarReproductor}
                  sx={{ color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '70vh',
                bgcolor: 'black'
              }}>
                <video
                  controls
                  controlsList="nodownload"
                  autoPlay
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '70vh',
                    width: 'auto',
                    height: 'auto'
                  }}
                >
                  <source 
                    src={construirUrlVideo(videoSeleccionado)} 
                    type="video/mp4" 
                  />
                  Tu navegador no soporta la reproducción de video.
                </video>
              </Box>
              
              <Box sx={{ p: 2, bgcolor: 'grey.900' }}>
                <Typography variant="h6" sx={{ color: 'white' }}>
                  {videoSeleccionado.nombreSinExtension}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ bgcolor: 'grey.900', p: 2 }}>
          <Button 
            onClick={handleCerrarReproductor}
            variant="contained"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RecursosMateriales;